import { ADMIN_ROLES, type Permission } from './permissions'
import { type User, type Role } from '@/stores/auth'

/**
 * Check if user has an admin role (super_admin, admin)
 * Admin users bypass all permission checks and have unrestricted access
 */
export const isAdmin = (user: User | null): boolean => {
  if (!user || !user.roles) return false
  return user.roles.some((role: Role) => ADMIN_ROLES.includes(role.name as any))
}

/** Spatie role name of the outside accountant who only works in the DATEV pages. */
export const DATEV_ACCOUNTANT_ROLE = 'Datev Accountant'

/**
 * A Datev Accountant is stored as an Employee (role_id 5), so `user.role` reads
 * 'employee'. The role list is what tells them apart.
 */
export const isDatevAccountant = (user: User | null): boolean =>
  !!user?.roles?.some((role: Role) => role.name === DATEV_ACCOUNTANT_ROLE)

/**
 * Extracts all permission names from a role
 */
const getRolePermissions = (role: Role): string[] => {
  return (role.permissions || []).map((p: any) => p.name)
}

/**
 * Extracts all permission names from user's explicit permissions
 */
const getUserPermissions = (user: User): string[] => {
  return (user.permissions || []).map((p: any) => p.name)
}

/**
 * Extracts all permission names from user's roles
 */
const getRolesPermissions = (user: User): string[] => {
  const permissions = new Set<string>()

  if (user.roles && user.roles.length > 0) {
    user.roles.forEach((role: Role) => {
      getRolePermissions(role).forEach((permission: string) => {
        permissions.add(permission)
      })
    })
  }

  return Array.from(permissions)
}

/**
 * Get all permissions for a user (combined from roles and direct permissions)
 */
export const getAllUserPermissions = (user: User | null): string[] => {
  if (!user) return []

  const permissionsSet = new Set<string>()

  // Add permissions from roles
  getRolesPermissions(user).forEach((p: string) => {
    permissionsSet.add(p)
  })

  // Add direct user permissions (may override or supplement role permissions)
  getUserPermissions(user).forEach((p: string) => {
    permissionsSet.add(p)
  })

  return Array.from(permissionsSet)
}

/**
 * Check if user has a specific permission
 * @param user - The user object from auth store
 * @param permission - The permission to check (e.g., 'restaurant:create')
 * @returns true if user has the permission or is an admin
 */
export const hasPermission = (user: User | null, permission: Permission | string): boolean => {
  if (!user) return false

  // Admin bypass: admins have all permissions
  if (isAdmin(user)) return true

  const allPermissions = getAllUserPermissions(user)
  return allPermissions.includes(permission)
}

/**
 * Check if user has ANY of the provided permissions (OR logic)
 * @param user - The user object from auth store
 * @param permissions - Array of permissions to check
 * @returns true if user has at least one permission or is an admin
 */
export const hasAnyPermission = (
  user: User | null,
  permissions: (Permission | string)[]
): boolean => {
  if (!user || permissions.length === 0) return false

  // Admin bypass: admins have all permissions
  if (isAdmin(user)) return true

  const allPermissions = getAllUserPermissions(user)
  return permissions.some((permission) => allPermissions.includes(permission))
}

/**
 * Check if user has ALL of the provided permissions (AND logic)
 * @param user - The user object from auth store
 * @param permissions - Array of permissions to check
 * @returns true if user has all permissions or is an admin
 */
export const hasAllPermissions = (
  user: User | null,
  permissions: (Permission | string)[]
): boolean => {
  if (!user || permissions.length === 0) return false

  // Admin bypass: admins have all permissions
  if (isAdmin(user)) return true

  const allPermissions = getAllUserPermissions(user)
  return permissions.every((permission) => allPermissions.includes(permission))
}

/**
 * Check if user has a specific role
 * @param user - The user object from auth store
 * @param roleName - The role name to check (e.g., 'super_admin', 'partner')
 * @returns true if user has the role or is an admin
 */
export const hasRole = (user: User | null, roleName: string): boolean => {
  if (!user || !user.roles) return false

  // Admin bypass: admins have all roles (except checking for admin role itself)
  if (isAdmin(user)) return true

  return user.roles.some((role: Role) => role.name === roleName)
}

/**
 * Check if user has ANY of the provided roles (OR logic)
 * @param user - The user object from auth store
 * @param roleNames - Array of role names to check
 * @returns true if user has at least one role or is an admin
 */
export const hasAnyRole = (user: User | null, roleNames: string[]): boolean => {
  if (!user || !user.roles || roleNames.length === 0) return false

  // Admin bypass: admins have all roles
  if (isAdmin(user)) return true

  return roleNames.some((roleName) => user.roles!.some((role: Role) => role.name === roleName))
}

/**
 * Check if user has ALL of the provided roles (AND logic)
 * @param user - The user object from auth store
 * @param roleNames - Array of role names to check
 * @returns true if user has all roles or is an admin
 */
export const hasAllRoles = (user: User | null, roleNames: string[]): boolean => {
  if (!user || !user.roles || roleNames.length === 0) return false

  // Admin bypass: admins have all roles
  if (isAdmin(user)) return true

  return roleNames.every((roleName) => user.roles!.some((role: Role) => role.name === roleName))
}

/**
 * Check if user can access a resource (combination of role and permission checks)
 * Useful for complex authorization scenarios
 * @param user - The user object from auth store
 * @param options - Configuration for authorization check
 * @returns true if user has access or is an admin
 */
export const canAccess = (
  user: User | null,
  options: {
    permissions?: (Permission | string)[]
    permissionLogic?: 'any' | 'all' // 'any' = OR logic, 'all' = AND logic
    roles?: string[]
    roleLogic?: 'any' | 'all' // 'any' = OR logic, 'all' = AND logic
    combineLogic?: 'and' | 'or' // How to combine role and permission checks
  }
): boolean => {
  if (!user) return false

  // Admin bypass: admins have access to everything
  if (isAdmin(user)) return true

  const { permissions = [], permissionLogic = 'any', roles = [], roleLogic = 'any', combineLogic = 'and' } = options

  const hasPermissionAccess =
    permissions.length === 0 ||
    (permissionLogic === 'any' ? hasAnyPermission(user, permissions) : hasAllPermissions(user, permissions))

  const hasRoleAccess =
    roles.length === 0 || (roleLogic === 'any' ? hasAnyRole(user, roles) : hasAllRoles(user, roles))

  if (combineLogic === 'and') {
    return hasPermissionAccess && hasRoleAccess
  } else {
    return hasPermissionAccess || hasRoleAccess
  }
}
